/**
 * Utility functions for managing anonymous vote tokens on the client side
 * Tokens are stored in cookies to persist across sessions
 */

const COOKIE_NAME = "bf6_anonymous_vote_token";
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year in seconds

/**
 * Get the anonymous vote token from cookies
 * Returns null if no token exists
 */
export function getAnonymousVoteToken(): string | null {
  if (typeof document === "undefined") {
    return null;
  }

  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === COOKIE_NAME) {
      return decodeURIComponent(value);
    }
  }

  return null;
}

/**
 * Set the anonymous vote token in a cookie
 * @param token - The token to store
 */
export function setAnonymousVoteToken(token: string): void {
  if (typeof document === "undefined") {
    return;
  }

  const expires = new Date();
  expires.setSeconds(expires.getSeconds() + COOKIE_MAX_AGE);

  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(token)}; path=/; expires=${expires.toUTCString()}; SameSite=Lax; Secure`;
}

/**
 * Delete the anonymous vote token cookie
 */
export function deleteAnonymousVoteToken(): void {
  if (typeof document === "undefined") {
    return;
  }

  document.cookie = `${COOKIE_NAME}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax; Secure`;
}

/**
 * Check if an anonymous vote token exists
 */
export function hasAnonymousVoteToken(): boolean {
  return getAnonymousVoteToken() !== null;
}
